/**
 * PROGRESS AGAINST THE CLOCK — what a card's percent-complete MEANS.
 *
 * A bare "40%" says nothing on its own. It earns its place by being read against
 * the card's own timeline: how far through the start->due window the calendar is,
 * versus how far the work actually is, and — at the rate it has moved so far —
 * whether it will land on time at all.
 *
 *   expected %  = how far through startsOn->dueDate today sits, counted in WORKING
 *                 days (the workspace calendar's — weekends and holidays don't count),
 *                 end-inclusive, the same span arithmetic the burndown ideal uses.
 *   behind      = the work is materially below what the calendar expects by now.
 *   won't make it = projecting the pace so far, the finish date falls after the
 *                 due date (this is the case Vinnie named: <50% done, due tomorrow
 *                 — it is not going to be done by tomorrow).
 *   overdue     = past the due date and not finished.
 *
 * The % is set by a person (the responsible doer, incl. a vendor, or a manager);
 * everything below is derived, never stored, so it is always current. Pure: no
 * store, no React — unit-tested in scripts/test-progress-health.ts.
 */
import { today } from './sprints';
import { asDate } from './utils';
import { workingDaysInclusive, addWorkingDays, DEFAULT_CALENDAR, type WorkCalendar } from './work-calendar';
import type { Issue } from './types';

/** How the card reads against its schedule. `none`/`tracking` carry no signal. */
export type ProgressHealthKind =
  | 'none' // no % recorded yet — show nothing
  | 'tracking' // a % is recorded but there is no due date to judge it against
  | 'done' // finished (resolved, or 100%)
  | 'on-track'
  | 'behind'
  | 'at-risk' // will miss the due date — pace-projected to be late, or already overdue
  | 'on-hold'; // paused on purpose — the clock is stopped, so it is not judged against it

export type ProgressTone = 'good' | 'warn' | 'bad' | 'neutral';

export interface ProgressHealth {
  kind: ProgressHealthKind;
  /** The recorded %, clamped 0..100 — null only when nothing is recorded. */
  pct: number | null;
  /** What the calendar expects by now (0..100) — null when there is no schedule. */
  expectedPct: number | null;
  /** Short word for a chip/dot: 'On track' | 'Behind' | "Won't make it" | 'Overdue' | 'Done'. */
  label: string;
  tone: ProgressTone;
  /** One plain-language line; empty for none/tracking. */
  reason: string;
  /** When at-risk by pace: the date it is projected to finish (YYYY-MM-DD). */
  projectedFinish?: string;
}

/** The fields this needs off a card — a Pick so callers pass the Issue straight in. */
export interface ProgressInput {
  progressPct?: number | null;
  startsOn?: string | null; // YYYY-MM-DD, the day work was meant to begin
  startedAt?: number | null; // ms, when work actually began (fallback start)
  createdAt?: number; // ms, creation (last-resort start)
  dueDate?: string | null; // YYYY-MM-DD, the target day
  resolved?: boolean; // the card is done / won't-do
}

/** Within this many points of the expected pace still reads as "On track". */
const ON_TRACK_TOL = 10;
/** Needing more than this multiple of the pace achieved so far = "Won't make it"
 *  (you would have to roughly double the pace you have held to finish on time). */
const WONT_MAKE_IT_FACTOR = 2;
/** Zero progress with at least this much of the window gone won't make it. */
const STALE_WINDOW = 50;

const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n));

/** A YYYY-MM-DD day as ms at local midnight, so it prints through the app's asDate. */
const dayMs = (day: string): number => {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1).getTime();
};
const human = (day: string) => asDate(dayMs(day));

export function progressHealth(
  card: ProgressInput,
  nowMs: number = Date.now(),
  cal: WorkCalendar = DEFAULT_CALENDAR,
): ProgressHealth {
  const raw = card.progressPct;
  if (raw == null || Number.isNaN(raw)) {
    return { kind: 'none', pct: null, expectedPct: null, label: '', tone: 'neutral', reason: '' };
  }
  const pct = clamp(Math.round(raw), 0, 100);

  if (card.resolved || pct >= 100) {
    return { kind: 'done', pct, expectedPct: null, label: 'Done', tone: 'good', reason: '' };
  }

  // The start reference: the day a person set, else when work actually began, else
  // creation. Without a start AND a due (and a sane order), there is nothing to
  // judge the % against — it just tracks.
  const start =
    card.startsOn ||
    (card.startedAt ? today(card.startedAt) : card.createdAt ? today(card.createdAt) : null);
  const due = card.dueDate || null;
  if (!start || !due || due < start) {
    return { kind: 'tracking', pct, expectedPct: null, label: '', tone: 'neutral', reason: '' };
  }

  // WORKING DAYS, not calendar days (Vinnie, 28 Oct): the calendar expects progress
  // only on days the workspace works, so a window's weekends and holidays don't count
  // toward "how far through we are". On the default Mon–Fri calendar that is simply
  // weekends; a workspace close pushes the expectation out. Guarded to ≥1 so a window
  // that is entirely non-working (a close over a weekend) never divides by zero.
  const now = today(nowMs);
  const total = Math.max(1, workingDaysInclusive(start, due, cal)); // >= 1, end-inclusive
  const elapsed = now < start ? 0 : workingDaysInclusive(start, now, cal);
  const expectedPct = clamp(Math.round((elapsed / total) * 100), 0, 100);

  // Already past the due date and not finished.
  if (now > due) {
    const over = workingDaysInclusive(due, now, cal) - 1; // working days past due
    // When there is progress to extrapolate from, an overdue card still has an
    // honest forecast: at the pace achieved so far it finishes ~this day. Surface
    // it (as "Won't make it" does) rather than leaving the forecast blank — a late
    // card that shows a slip but no finish date left the reader asking "so when?".
    // With no progress yet (pct 0) the pace is zero and nothing can be projected.
    const achieved = elapsed > 0 ? pct / elapsed : 0; // points per working day done so far
    const projected = achieved > 0 ? addWorkingDays(now, Math.ceil((100 - pct) / achieved), cal) : undefined;
    const base =
      over <= 0
        ? `${pct}% done and due today — not finished.`
        : `${pct}% done, ${over} day${over === 1 ? '' : 's'} overdue.`;
    return {
      kind: 'at-risk',
      pct,
      expectedPct,
      label: 'Overdue',
      tone: 'bad',
      ...(projected ? { projectedFinish: projected } : {}),
      reason: projected ? `${base.replace(/\.$/, '')} — at this pace, finishes ~${human(projected)}.` : base,
    };
  }

  // No progress recorded yet: once past the window's midpoint with nothing done,
  // it won't make it; before that it is behind (or still fine very early on).
  if (pct === 0) {
    if (expectedPct >= STALE_WINDOW) {
      return {
        kind: 'at-risk',
        pct,
        expectedPct,
        label: "Won't make it",
        tone: 'bad',
        reason: `No progress recorded, due ${human(due)} — about ${expectedPct}% expected by now.`,
      };
    }
    if (expectedPct > ON_TRACK_TOL) {
      return {
        kind: 'behind',
        pct,
        expectedPct,
        label: 'Behind',
        tone: 'warn',
        reason: `Not started yet — about ${expectedPct}% expected by now.`,
      };
    }
    return { kind: 'on-track', pct, expectedPct, label: 'On track', tone: 'good', reason: `Due ${human(due)}.` };
  }

  // How much faster than the pace achieved so far would you have to go to finish by
  // the due date? Needing to go much faster (> WONT_MAKE_IT_FACTOR) reads as "Won't
  // make it"; a little faster is only "Behind"; at or under pace is on track.
  const daysLeft = Math.max(1, workingDaysInclusive(now, due, cal)); // >= 1, working days left
  if (elapsed > 0) {
    const achieved = pct / elapsed; // points per working day done so far
    const required = (100 - pct) / daysLeft; // points per working day needed from here
    if (required > achieved * WONT_MAKE_IT_FACTOR) {
      const projected = addWorkingDays(now, Math.ceil((100 - pct) / achieved), cal);
      return {
        kind: 'at-risk',
        pct,
        expectedPct,
        label: "Won't make it",
        tone: 'bad',
        projectedFinish: projected,
        reason: `${pct}% done, due ${human(due)} — at this pace, finishes ~${human(projected)}.`,
      };
    }
  }

  // On pace to land near the due date, but is it lagging the calendar?
  if (expectedPct - pct > ON_TRACK_TOL) {
    return {
      kind: 'behind',
      pct,
      expectedPct,
      label: 'Behind',
      tone: 'warn',
      reason: `${pct}% done, about ${expectedPct}% expected by now.`,
    };
  }

  return {
    kind: 'on-track',
    pct,
    expectedPct,
    label: 'On track',
    tone: 'good',
    reason: `${pct}% done, about ${expectedPct}% expected by now.`,
  };
}

/** The slice of a Phase this read falls back to — a card with no date of its own
 *  is judged against its phase's window. Both are plain `YYYY-MM-DD` days, or
 *  absent/null when the phase has not set them. */
export interface ProgressPhaseWindow {
  startsOn?: string | null;
  targetEnd?: string | null;
}

/**
 * THE APP ADAPTER — the same read, straight off an Issue. Keeps the one place that
 * knows a done/won't-do card is "resolved", so every surface agrees. (The pure
 * `progressHealth` above stays free of the Issue type, for the unit tests.)
 *
 * PHASE-WINDOW FALLBACK. A card's OWN dates always win; where it has none, the
 * card's phase lends its window so a phased, date-less card is still judged
 * rather than left tracking:
 *   start = card.startsOn || phase.startsOn || (card.startedAt → day) || (card.createdAt → day)
 *   due   = card.dueDate  || phase.targetEnd
 * Pass `phase` as the card's Phase (or just its `{ startsOn, targetEnd }`); omit
 * it where no phase is in scope and the read is unchanged. With neither a card nor
 * a phase due, the read stays 'tracking', exactly as before.
 */
export function healthOfIssue(
  issue: Pick<Issue, 'progressPct' | 'startsOn' | 'startedAt' | 'createdAt' | 'dueDate' | 'status' | 'heldAt'>,
  nowMs: number = Date.now(),
  phase?: ProgressPhaseWindow | null,
  cal: WorkCalendar = DEFAULT_CALENDAR,
): ProgressHealth {
  // ON HOLD short-circuits every other read. A deliberately paused card has its
  // clock stopped (project-card-hold), so judging it behind/at-risk would be wrong
  // and double-counts it in the portfolio slip — it reads a neutral "On hold"
  // instead, and because that is neither 'behind' nor 'at-risk' it drops out of the
  // at-risk counts on its own. The % it had is kept for display, not judged.
  if (issue.heldAt) {
    return { kind: 'on-hold', pct: issue.progressPct ?? null, expectedPct: null, label: 'On Hold', tone: 'neutral', reason: '' };
  }
  return progressHealth(
    {
      progressPct: issue.progressPct,
      // The card's own start wins; the phase's start is the fallback, ahead of the
      // startedAt/createdAt last resorts progressHealth applies when this is null.
      startsOn: issue.startsOn || phase?.startsOn || null,
      startedAt: issue.startedAt,
      createdAt: issue.createdAt,
      // The card's own due wins; the phase's targetEnd is the fallback.
      dueDate: issue.dueDate || phase?.targetEnd || null,
      resolved: issue.status === 'done' || issue.status === 'wontdo',
    },
    nowMs,
    cal,
  );
}

/**
 * HOW A WHOLE PHASE OR PROJECT READS AGAINST ITS CALENDAR — the aggregate cousin
 * of the per-card read above, in plain words.
 *
 * A single card gets the full treatment (pace projection, "won't make it"). A
 * phase or a project is MANY cards at once, so projecting a pace per card and
 * summing is noise; the honest aggregate question is simpler and is the one
 * Vinnie asked for: how far through the window are we versus how much is done?
 *
 *   behindBy = expected% - actual%   (positive = behind the calendar, negative = ahead)
 *
 * The verdict is deliberately coarse — a number rounded to the nearest 5, spoken,
 * never an index like "SPI 0.72":
 *   within ON_TRACK_TOL points either way → "On schedule"
 *   more than that behind                 → "About N% behind schedule" (warn)
 *   more than that ahead                  → "About N% ahead"           (good)
 *   the window has fully elapsed, not done → "Past its window"         (bad)
 * The tolerance is the SAME ON_TRACK_TOL the per-card read uses, so a phase's
 * schedule line never contradicts its cards' health dots at the boundary.
 */
export type ScheduleHealthKind = 'none' | 'done' | 'on-schedule' | 'behind' | 'ahead' | 'past-window';

export interface ScheduleHealth {
  kind: ScheduleHealthKind;
  /** expected% - actual%, or null when there is no window / nothing measured. */
  behindBy: number | null;
  /** Spoken verdict: '' for none; else 'On schedule' | 'About N% behind schedule' | … */
  label: string;
  tone: ProgressTone;
}

const round5 = (n: number) => Math.round(n / 5) * 5;

/**
 * How far through a plain `YYYY-MM-DD` window today sits, 0..100 — the same
 * end-inclusive WORKING-day span arithmetic a single card's expected% uses (the
 * workspace calendar's working days; weekends and holidays don't count). null when
 * the window is absent or inverted (end before start). Before the window opens reads
 * 0; once the end day is reached or passed reads 100.
 */
export function expectedPctOfWindow(
  startsOn: string | null | undefined,
  targetEnd: string | null | undefined,
  nowMs: number = Date.now(),
  cal: WorkCalendar = DEFAULT_CALENDAR,
): number | null {
  if (!startsOn || !targetEnd || targetEnd < startsOn) return null;
  const now = today(nowMs);
  const total = Math.max(1, workingDaysInclusive(startsOn, targetEnd, cal)); // >= 1, working days
  const elapsed = now < startsOn ? 0 : workingDaysInclusive(startsOn, now, cal);
  return clamp(Math.round((elapsed / total) * 100), 0, 100);
}

/**
 * The aggregate schedule read. `actualPct` is the group's progress (the phase bar
 * / project %), `expectedPct` is expectedPctOfWindow for its date window, and
 * `allDone` says every card in the group is resolved (so a finished group reads
 * "Done", never "past its window"). With no window or nothing measured the read
 * carries no signal ('none', empty label) and the surface shows nothing.
 */
export function scheduleHealth(
  actualPct: number | null,
  expectedPct: number | null,
  allDone: boolean,
): ScheduleHealth {
  if (allDone) return { kind: 'done', behindBy: null, label: 'Done', tone: 'good' };
  if (expectedPct == null || actualPct == null) {
    return { kind: 'none', behindBy: null, label: '', tone: 'neutral' };
  }
  const behindBy = expectedPct - actualPct;
  // The planned time is fully spent and the work is not finished — the clearest
  // red there is, whatever the % reached.
  if (expectedPct >= 100) {
    return { kind: 'past-window', behindBy, label: 'Past its window', tone: 'bad' };
  }
  if (behindBy > ON_TRACK_TOL) {
    return { kind: 'behind', behindBy, label: `About ${round5(behindBy)}% behind schedule`, tone: 'warn' };
  }
  if (behindBy < -ON_TRACK_TOL) {
    return { kind: 'ahead', behindBy, label: `About ${round5(-behindBy)}% ahead`, tone: 'good' };
  }
  return { kind: 'on-schedule', behindBy, label: 'On schedule', tone: 'good' };
}

/** Behind the clock by more than this share of the whole scope's worth of work is
 *  no longer a wobble — it tips the aggregate schedule read from amber to red. A
 *  quarter missing against the calendar is the line. */
export const SCHEDULE_BEHIND_RED_PCT = 25;

/**
 * The aggregate schedule read as a three-level severity (green/amber/red), so a
 * steering verdict can fold card-progress-vs-clock into the worst-of beside the
 * phase-date and pace-forecast reads (portfolio ProjectStatus, finding 3 / Vinnie
 * 29 Oct). Its point is that — unlike the Monte-Carlo forecast band — it needs NO
 * finished-work history, so it still bites in cold-start: a board a third behind
 * the clock can no longer read "On track" just because its phase dates are nominally
 * still ahead and the forecast has not warmed up. 'Past its window' (planned time
 * fully spent, work still open) and a materially-behind board are red; any lesser
 * shortfall is amber; on-schedule / ahead / done / no-window carry no alarm.
 * Returns the Rag literal union (identical to portfolio's Rag) without importing it,
 * so progress-health stays free of the portfolio layer. Pure — tested in
 * scripts/test-progress-health.ts.
 *
 * TODO(pace.ts): when FD2's shared `pacePicture(scope, now, planDate, cal)` primitive
 * lands ([[project-milestone-mechanism-and-order]]), fold THIS cold-start schedule read
 * into it so the progress-vs-clock severity and the Monte-Carlo forecast draw from one
 * source — the schedule floor should not live in two places once pace.ts exists.
 */
export function scheduleHealthSeverity(h: ScheduleHealth): 'green' | 'amber' | 'red' {
  if (h.kind === 'past-window') return 'red';
  if (h.kind === 'behind') return (h.behindBy ?? 0) > SCHEDULE_BEHIND_RED_PCT ? 'red' : 'amber';
  return 'green';
}
